// <OrgGridToolbar> — Org View Step 4 (T-050, plan §6.1 / spec §4 + §6.1).
//
// Two pieces glued together:
//   1. The page header — title "People" + Add Employee CTA (spec §4)
//   2. The toolbar row above the grid — result count + view-mode
//      toggle (spec §6.1)
//
// Sort dropdown (§6.1.1) and density toggle (§6.1.3) are deferred to
// T-054 pixel polish — the plan §11 step-4 scope is explicit about the
// view-mode toggle + count + page rename.

import { Plus } from 'lucide-react';
import { OrgSortDropdown, type OrgSortKey } from './OrgSortDropdown';
import { OrgDensityToggle, type OrgDensity } from './OrgDensityToggle';

export type OrgViewMode = 'cards' | 'table';

export interface OrgGridToolbarProps {
  /** Total result count from the server. The "{N} PEOPLE" label is
   *  hidden when 0 — the empty state owns that real estate (spec §6.3). */
  total: number;
  viewMode: OrgViewMode;
  onViewModeChange: (next: OrgViewMode) => void;
  onAddEmployee: () => void;
  /** Sort key (T-054 / spec §6.1.1). When omitted, the dropdown is
   *  hidden — keeps unit tests on the toolbar simple. */
  sort?: OrgSortKey;
  onSortChange?: (next: OrgSortKey) => void;
  /** Density choice (T-054 / spec §6.1.3). Hidden when omitted. The
   *  toggle is muted automatically when `viewMode === 'table'`. */
  density?: OrgDensity;
  onDensityChange?: (next: OrgDensity) => void;
}

export function OrgGridToolbar({
  total,
  viewMode,
  onViewModeChange,
  onAddEmployee,
  sort,
  onSortChange,
  density,
  onDensityChange,
}: OrgGridToolbarProps) {
  return (
    <div data-testid="org-grid-toolbar">
      {/* Header — spec §4 */}
      <header className="px-10 py-8 flex justify-between items-center bg-white border-b border-slate-100">
        <h2
          className="text-3xl font-bold text-slate-800 tracking-tight"
          data-testid="org-page-title"
        >
          People
        </h2>
        <button
          type="button"
          onClick={onAddEmployee}
          data-testid="org-add-employee-cta"
          className="flex items-center gap-2 bg-[#6344C6] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-[#553ABE] active:scale-95 shadow-md hover:shadow-lg transition"
        >
          <Plus size={16} aria-hidden />
          <span>Add Employee</span>
        </button>
      </header>

      {/* Toolbar row — spec §6.1 */}
      <div className="flex justify-between items-center mb-6 mt-6 px-10">
        {total > 0 ? (
          <p
            data-testid="org-result-count"
            className="text-sm font-bold text-slate-400 uppercase tracking-widest"
          >
            {total} {total === 1 ? 'person' : 'people'}
          </p>
        ) : (
          // Reserve the row's vertical space so the view-toggle doesn't
          // jump up when the count disappears.
          <span aria-hidden />
        )}
        <div className="flex items-center gap-2">
          {sort !== undefined && onSortChange ? (
            <OrgSortDropdown value={sort} onChange={onSortChange} />
          ) : null}
          <ViewModeToggle value={viewMode} onChange={onViewModeChange} />
          {density !== undefined && onDensityChange ? (
            <OrgDensityToggle
              value={density}
              onChange={onDensityChange}
              disabled={viewMode === 'table'}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ViewModeToggle({
  value,
  onChange,
}: {
  value: OrgViewMode;
  onChange: (next: OrgViewMode) => void;
}) {
  return (
    <div
      role="group"
      aria-label="View mode"
      className="inline-flex items-center bg-slate-100 rounded-lg p-1"
      data-testid="org-view-toggle"
    >
      <ToggleButton
        active={value === 'cards'}
        onClick={() => onChange('cards')}
        testId="org-view-toggle-cards"
      >
        Cards
      </ToggleButton>
      <ToggleButton
        active={value === 'table'}
        onClick={() => onChange('table')}
        testId="org-view-toggle-table"
      >
        Table
      </ToggleButton>
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  children,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testId}
      className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
        active
          ? 'bg-white shadow-sm text-slate-800'
          : 'text-slate-500 hover:text-slate-800'
      }`}
    >
      {children}
    </button>
  );
}
